import config from 'config';
import {
  CustomMenuItem,
  DigitalMap,
  DigitalMapFloorOverlay,
  DigitalMapLocation,
  DigitalMapPin,
  Schedule,
} from 'models/digitalMap';
import moment, { Moment } from 'moment-timezone';
import { TFunction } from 'react-i18next';
import { Weekday } from 'models/product';
import { Filters } from './useUrlQueryDisplayState';
import { GetSettingsResponse } from 'models/settings';

export const formattedTimeOfDay = (time: string, language: string) => {
  if (!time) {
    return '';
  }

  if (language === 'en-US') {
    return moment(time, 'HH:mm').locale('en-US').format('h:mm a');
  }

  return moment(time, 'HH:mm').format('H:mm');
};

type DateFormat = 'D MMM YYYY' | 'D MMM' | 'MMM D';

export const formattedDate = (date: string, dateFormat: DateFormat, language: string) => {
  if (!date) {
    return '';
  }

  switch (dateFormat) {
    case 'D MMM YYYY':
      if (language === 'ja-JP') {
        return moment(date).locale('ja-JP').format('YYYY年M月D日');
      }
      return moment(date).locale(language).format('D MMM YYYY');
    case 'D MMM':
      if (language === 'ja-JP') {
        return moment(date).locale('ja-JP').format('M月D日');
      }
      return moment(date).locale(language).format('D MMM');
    case 'MMM D':
      if (language === 'ja-JP') {
        return moment(date).locale('ja-JP').format('M月D日');
      }
      return moment(date).locale(language).format('MMM D');
    default:
      throw new Error(`Unsupported date format: ${dateFormat}`);
  }
};

export const formattedTravelTime = (travelTime: string, t: TFunction) => {
  const minutes = travelTime?.replace('m', '');

  if (minutes) {
    return t('{{minutes}}m', { minutes });
  }

  return travelTime;
};

export const getSearchFilteredPins = (
  pins: DigitalMapPin[],
  searchText: string
): DigitalMapPin[] => {
  if (!searchText) {
    return pins;
  }

  return (
    pins?.filter((pin) => {
      return (
        pin.title?.toLowerCase().includes(searchText.toLowerCase()) ||
        pin.description?.toLowerCase().includes(searchText.toLowerCase()) ||
        pin.tags?.some((tag) => tag.toLowerCase().includes(searchText.toLowerCase())) ||
        pin.category === searchText
      );
    }) ?? []
  );
};

// findActivePinSchedule returns the first rule that is active for the given moment.
export const findActivePinSchedule = <T extends { schedule: Schedule }>(
  scheduleRules: T[],
  m: Moment
): T | null => {
  const date = m.format('YYYY-MM-DD');
  const weekday = m.locale('en').format('ddd').toUpperCase() as Weekday;

  for (const scheduleRule of scheduleRules) {
    const schedule = scheduleRule.schedule;

    for (const dateRange of schedule.date_ranges) {
      if (dateRange.start_date_local > date || dateRange.end_date_local < date) {
        continue;
      }

      if (!schedule.days_of_week.includes(weekday)) {
        continue;
      }

      if (schedule.closed_dates.includes(date)) {
        continue;
      }

      if (schedule.annually_repeating_closed_dates.some((d) => d.endsWith(date.slice(5)))) {
        continue;
      }

      return scheduleRule;
    }
  }

  return null;
};

export const getFilteredPins = (
  pins: DigitalMapPin[],
  filters: Filters,
  searchText: string,
  selectedFloor?: number,
  isStarred?: (pinKey: string) => boolean
): DigitalMapPin[] => {
  return (
    getSearchFilteredPins(pins, searchText)
      ?.filter((pin) => {
        if (filters.categories.length === 0) {
          return pin.category !== 'TRANSPORT';
        }

        if (filters.categories.includes('WAIT_TIME')) {
          return pin.show_wait_time;
        }

        if (filters.categories.includes('TRANSPORT')) {
          return (
            pin.category === 'TRANSPORT' &&
            pin.bus_route_ids?.some(
              (routeId) => filters.busRouteId === routeId || filters.busRouteId == null
            )
          );
        }

        return filters.categories?.includes(pin.category);
      })
      ?.filter((pin) => {
        if (filters.attributes.length === 0) {
          return true;
        }

        return (
          (!filters.attributes.includes('hasTickets') || pin.associated_product_ids?.length > 0) &&
          (!filters.attributes.includes('hasMobileOrders') || !!pin.online_order_restaurant_id) &&
          (!filters.attributes.includes('starred') || isStarred?.(pin.key))
        );
      })
      ?.filter((pin) => {
        if (filters.tags.length === 0) {
          return true;
        }

        return pin.tags?.some((tag) => filters.tags.includes(tag)) ?? false;
      })
      ?.filter((pin) => {
        if (filters.structuredInfoItems.length === 0) {
          return true;
        }

        return filters.structuredInfoItems.every((filter) => {
          if (filter.values.length === 0) {
            return true;
          }

          const structuredInfoItem = pin.structured_info_items?.find(
            (item) => item.info_category_key === filter.info_category_key
          );

          if (!structuredInfoItem) {
            return false;
          }

          return filter.values.some((value) => structuredInfoItem.values?.includes(value));
        });
      })
      ?.filter((pin) => {
        if (filters.openNow) {
          const openHoursForToday = findActivePinSchedule(pin.open_hour_schedules, moment());
          return (
            openHoursForToday &&
            moment(openHoursForToday?.start_time_local, 'HH:mm').isSameOrBefore(moment()) &&
            moment(openHoursForToday?.end_time_local, 'HH:mm').isSameOrAfter(moment())
          );
        }

        if (!filters.openAt) {
          return true;
        }

        const openHours = findActivePinSchedule(pin.open_hour_schedules, moment());
        if (!openHours?.start_time_local || !openHours?.end_time_local) {
          return false;
        }

        const openAt = moment(filters.openAt, 'HH:mm');

        return (
          moment(openHours?.start_time_local, 'HH:mm').isSameOrBefore(openAt) &&
          moment(openHours?.end_time_local, 'HH:mm').isSameOrAfter(openAt)
        );
      })
      ?.filter((pin) => {
        if (!filters.showTimeFrom && !filters.showTimeTo) {
          return true;
        }

        const showtimes = findActivePinSchedule(pin.showtime_schedules, moment())?.showtimes ?? [];

        if (filters.showTimeFrom && filters.showTimeTo) {
          return showtimes?.some(
            (showtime) =>
              moment(showtime, 'HH:mm').isSameOrAfter(moment(filters.showTimeFrom, 'HH:mm')) &&
              moment(showtime, 'HH:mm').isSameOrBefore(moment(filters.showTimeTo, 'HH:mm'))
          );
        } else if (filters.showTimeTo) {
          return showtimes?.some((showtime) =>
            moment(showtime, 'HH:mm').isSameOrBefore(moment(filters.showTimeTo, 'HH:mm'))
          );
        }

        return showtimes?.some((showtime) =>
          moment(showtime, 'HH:mm').isSameOrAfter(moment(filters.showTimeFrom, 'HH:mm'))
        );
      })
      ?.filter((pin) => {
        if (!selectedFloor) {
          return true;
        }

        return pin.floor_number === selectedFloor;
      }) ?? []
  );
};

export const getDistanceText = (
  distanceInMeters: number,
  language: string,
  t: TFunction
): string => {
  // If language is English, return the distance in miles
  if (language === 'en-US') {
    return t('{{distanceInMiles}}mi', { distanceInMiles: (distanceInMeters / 1609).toFixed(1) });
  }

  return t('{{distanceInKm}}km', { distanceInKm: (distanceInMeters / 1000).toFixed(1) });
};

export const outOfRange = (
  position: google.maps.LatLng | null,
  location: DigitalMapLocation | null,
  activationRange: number
): boolean => {
  if (!position) {
    return true;
  }

  const distanceBetweenPoints = window.google.maps.geometry.spherical.computeDistanceBetween(
    position,
    new google.maps.LatLng({
      lat: location?.latitude ?? 0,
      lng: location?.longitude ?? 0,
    })
  );

  return distanceBetweenPoints > activationRange;
};

export const getFloorOverlay = (
  digitalMap: DigitalMap | null,
  selectedFloor: number
): DigitalMapFloorOverlay | null => {
  if (!digitalMap) {
    return null;
  }

  if (digitalMap?.background?.do_not_use_overlay_image) {
    return null;
  }

  if ((digitalMap?.map_floor_count ?? 0) > 1 && selectedFloor > 1) {
    return (
      digitalMap?.floor_overlays?.find((overlay) => overlay.floor_number === selectedFloor) ?? null
    );
  }

  return {
    floor_number: 1,
    image_url: digitalMap?.background?.image_url ?? '',
    image_aspect_ratio: digitalMap?.background?.image_aspect_ratio,
    image_projection_width_in_meters: digitalMap?.background?.image_projection_width_in_meters ?? 0,
    top_left: digitalMap?.background?.top_left,
  };
};

export const getPreloadTargets = (
  digitalMap: DigitalMap | null,
  settings: GetSettingsResponse | null
): PreloadTargets => {
  const editorJsImageUrls = digitalMap?.pins
    .flatMap((pin) =>
      pin.free_form_content_items.flatMap((item) => {
        const parsedData = JSON.parse(item.data);
        return parsedData?.blocks
          ?.filter((block: any) => {
            const url = block.data?.file?.url;
            return block.type === 'image' && url && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
          })
          .map((block: any) => block.data.file.url);
      })
    )
    .flat()
    .filter((url): url is string => typeof url === 'string');

  const editorJsVideoUrls = digitalMap?.pins
    .flatMap((pin) =>
      pin.free_form_content_items.flatMap((item) => {
        const parsedData = JSON.parse(item.data);
        return parsedData?.blocks
          ?.filter((block: any) => {
            const url = block.data?.file?.url;
            return block.type === 'image' && url && /\.(mp4|webm|ogg)$/i.test(url);
          })
          .map((block: any) => block.data.file.url);
      })
    )
    .flat()
    .filter((url): url is string => typeof url === 'string');

  const editorJsAudioUrls = digitalMap?.pins
    .flatMap((pin) =>
      pin.free_form_content_items.flatMap((item) => {
        const parsedData = JSON.parse(item.data);
        return parsedData?.blocks
          ?.filter((block: any) => {
            const url = block.data?.file?.url;
            return block.type === 'image' && url && /\.(mp3|wav|ogg)$/i.test(url);
          })
          .map((block: any) => block.data.file.url);
      })
    )
    .flat()
    .filter((url): url is string => typeof url === 'string');

  const pinThumbnails = digitalMap?.pins
    ?.filter((pin) => pin.use_thumbnail_image_pin_marker)
    .map((pin) => {
      let thumbnailUrl = pin.thumbnail_url;
      if (pin.media_items?.length) {
        const thumbnailImgUrl = pin.media_items?.find((media) => media.type === 'IMAGE')?.url;
        if (thumbnailImgUrl) {
          thumbnailUrl = thumbnailImgUrl;
        }
      }
      return thumbnailUrl;
    });

  const pinDetailsFirstImages = digitalMap?.pins
    .map((pin) => {
      return pin.media_items
        ?.filter((media) => media.type === 'IMAGE')
        ?.slice(0, 1)
        ?.map((media) => media.url);
    })
    .flat();

  const pinDetailsSecondaryImages = digitalMap?.pins
    .map((pin) => {
      return pin.media_items
        ?.filter((media) => media.type === 'IMAGE')
        ?.slice(1)
        ?.map((media) => media.url);
    })
    .flat();

  const categoryIcons = digitalMap?.pin_categories?.map((category) => category.icon_url);

  const menuIcons = [
    '/static/images/ic_gift.svg',
    '/static/images/ic_gift_bg.svg',
    '/static/images/map/all_icon.svg',
    '/static/images/map/back_arrow.svg',
    '/static/images/map/brush.svg',
    '/static/images/map/calendar_white.svg',
    '/static/images/map/close.svg',
    '/static/images/map/down_chevron.svg',
    '/static/images/map/download.svg',
    '/static/images/map/filter.svg',
    '/static/images/map/filter_white.svg',
    '/static/images/map/fork_spoon_white.svg',
    '/static/images/map/gift.svg',
    '/static/images/map/globe.svg',
    '/static/images/map/mail-send.svg',
    '/static/images/map/map_pin.svg',
    '/static/images/map/map_pin_bold.svg',
    '/static/images/map/notification_bell.svg',
    '/static/images/map/nutmeg-ai-logo.svg',
    '/static/images/map/schedule.svg',
    '/static/images/map/search.svg',
    '/static/images/map/stamp_rally.svg',
    '/static/images/map/ticket.svg',
    '/static/images/map/ticket_white.svg',
    '/static/images/map/trash.svg',
    '/static/images/map/up_arrow.svg',
    '/static/images/map/user_circle.svg',
    '/static/images/map/walker_blue.svg',
  ];

  const overlayImages: string[] = [
    digitalMap?.background?.image_url,
    ...(digitalMap?.floor_overlays?.map((overlay) => overlay.image_url) ?? []),
  ].filter((url): url is string => typeof url === 'string' && url !== '');

  const logoImageUrl = settings?.logo_url ?? null;

  return {
    editorJsImageUrls: editorJsImageUrls || [],
    editorJsVideoUrls: editorJsVideoUrls || [],
    editorJsAudioUrls: editorJsAudioUrls || [],
    pinThumbnails: pinThumbnails || [],
    pinDetailsFirstImages: pinDetailsFirstImages || [],
    pinDetailsSecondaryImages: pinDetailsSecondaryImages || [],
    categoryIcons: categoryIcons || [],
    menuIcons,
    overlayImages,
    logoImageUrl,
  };
};

export interface PreloadTargets {
  editorJsImageUrls: string[];
  editorJsVideoUrls: string[];
  editorJsAudioUrls: string[];
  pinThumbnails: string[];
  pinDetailsFirstImages: string[];
  pinDetailsSecondaryImages: string[];
  categoryIcons: string[];
  menuIcons: string[];
  overlayImages: string[];
  logoImageUrl: string | null;
}

export const getCustomMenuItems = (digitalMap: DigitalMap | null): CustomMenuItem[] => {
  if (
    digitalMap?.display_settings?.custom_menu_items &&
    digitalMap?.display_settings?.custom_menu_items.length > 0
  ) {
    return digitalMap?.display_settings?.custom_menu_items;
  }

  if (
    digitalMap?.display_settings?.menu_items &&
    digitalMap?.display_settings?.menu_items.length > 0
  ) {
    return digitalMap?.display_settings?.menu_items.map((menuItem) => ({
      label: menuItem,
      url: '',
      item_type: menuItem,
      sub_menu_items: [],
    }));
  }

  return ([
    'MAP',
    'MY_ACCOUNT',
    'TICKETS',
    'SCHEDULE',
    'STAMP_RALLY',
    ...(config.enableMapItineraryCreator ? ['ITINERARIES'] : []),
  ] as CustomMenuItem['item_type'][])
    .filter(
      (menuItem) =>
        !digitalMap?.display_settings?.menu_items?.length ||
        digitalMap?.display_settings?.menu_items?.includes(menuItem as any)
    )
    .map((menuItem) => ({
      label: menuItem,
      url: '',
      item_type: menuItem,
      sub_menu_items: [],
    }));
};

export const isItineraryCreatorEnabled = (digitalMap: DigitalMap | null): boolean => {
  return (
    config.enableMapItineraryCreator &&
    getCustomMenuItems(digitalMap).some((menuItem) => menuItem.item_type === 'ITINERARIES')
  );
};
